import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import dayjs from "dayjs";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setHeaderCollapse } from "../../../../core/data/redux/commonSlice";
import { AuthContext } from "../../../../context/authcontext";
import { all_routes } from "../../../router/all_routes";
import {
  HeaderAction,
  ExportDropdown,
  ExportKind,
  useTemplateFont,
  TEMPLATE_FONT_FAMILY,
} from "../common";
import { GranularityToggle } from "../chartControls";
import {
  fetchRevenueSummary,
  exportRevenueSummaryExcel,
  RevenueSummary as RevenueSummaryData,
  RevenuePeriod,
} from "./revenueSummaryService";
import { downloadExportFile } from "../dashboardApi";
import { useMoneyFormat } from "../../../../components/currencyFormat";
import { getLifecycleLabels } from "../../../../utils/lifecycleUtils";
import { useSalesUserId } from "../../../../utils/salesUserScope";
import { exportDashboardPdf } from "../exportDashboardPdf";
import DashboardFilterDropdown, {
  ActiveFilterChips,
  useDashboardFilters,
  hasFilterChips,
  describeFilters,
  toApiFilters,
} from "../filters/DashboardFilterDropdown";
import {
  palette,
  productGradients,
  teamColors,
  overviewMeta,
  defaultOverviewMeta,
  comparisonMeta,
} from "./constants";
import RevenueSummarySkeleton from "./RevenueSummarySkeleton";
import "./revenueSummary.css";

const route = all_routes;

const overviewColumnClasses = ["pe-md-0", "px-md-0", "ps-md-0"];

type CurrencyFormatter = (value: number) => string;

const parseGrowth = (growth?: string | number | null) => {
  const parsed = parseFloat(String(growth ?? ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const isNegativeGrowth = (growth?: string | number | null) => (parseGrowth(growth) ?? 0) < 0;

const formatGrowth = (growth?: string | number | null) => {
  const text = String(growth ?? "").trim();
  if (!text) return "0%";
  if (/^[+-]/.test(text)) return text;
  return (parseGrowth(text) ?? 0) > 0 ? `+${text}` : text;
};

const growthBadgeClass = (growth?: string | number | null) =>
  isNegativeGrowth(growth) ? "badge-soft-danger" : "badge-soft-success";

const formatTrendDate = (date: string, period: RevenuePeriod) => {
  if (period === "by_year") return date;
  const parsed = dayjs(date);
  if (!parsed.isValid()) return date;
  return parsed.format(period === "by_week" ? "DD MMM" : "MMM YYYY");
};

const niceMax = (value: number, ticks: number) => {
  if (!(value > 0)) return ticks;
  const rawStep = value / ticks;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  return Math.ceil(rawStep / magnitude) * magnitude * ticks;
};

const countFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

const sparklineXAxis: ApexXAxis = {
  labels: { show: false },
  axisBorder: { show: false },
  axisTicks: { show: false },
  tooltip: { enabled: false },
};

const sparklineGrid: ApexGrid = {
  show: false,
  padding: { top: 0, right: 0, bottom: 0, left: 0 },
};

const LABEL_MAX_LENGTH = 16;

const truncateLabel = (text: string) =>
  text.length > LABEL_MAX_LENGTH ? `${text.slice(0, LABEL_MAX_LENGTH).trimEnd()}…` : text;

const attachLabelTitles = (chartContext: any, fullNames: string[]) => {
  const root: HTMLElement | undefined = chartContext?.el;
  if (!root) return;
  const byShortName = new Map(fullNames.map((name) => [truncateLabel(name), name]));
  root.querySelectorAll(".apexcharts-yaxis-label").forEach((label) => {
    const shortName = (label.querySelector("tspan")?.textContent ?? label.textContent ?? "").trim();
    const fullName = byShortName.get(shortName);
    if (!fullName) return;
    label.querySelectorAll("title").forEach((title) => title.remove());
    const title = document.createElementNS("http://www.w3.org/2000/svg", "title");
    title.textContent = fullName;
    label.appendChild(title);
    (label as SVGElement).style.cursor = fullName === shortName ? "" : "help";
  });
};

const horizontalBarBase = (categories: string[], max: number, formatCurrency: CurrencyFormatter): ApexOptions => ({
  chart: {
    type: "bar",
    toolbar: { show: false },
    zoom: { enabled: false },
    foreColor: palette.axis,
    fontFamily: TEMPLATE_FONT_FAMILY,
    events: {
      mounted: (chartContext: any) => attachLabelTitles(chartContext, categories),
      updated: (chartContext: any) => attachLabelTitles(chartContext, categories),
    },
  },
  plotOptions: {
    bar: { horizontal: true, distributed: true, barHeight: "56%", borderRadius: 6, borderRadiusApplication: "around" },
  },
  dataLabels: { enabled: false },
  legend: { show: false },
  states: { active: { filter: { type: "none" } } },
  grid: { show: false, padding: { top: -10, right: 18, left: 14 } },
  xaxis: {
    categories,
    min: 0,
    max,
    tickAmount: 4,
    labels: { formatter: (val: string) => formatCurrency(Number(val)), style: { fontSize: "11px" } },
    axisBorder: { show: true, color: palette.axisLine },
    axisTicks: { show: true, color: "#F3F3F3" },
  },
  yaxis: {
    axisBorder: { show: true, color: "#E3E3E3" },
    labels: {
      style: { fontSize: "11px" },
      maxWidth: 160,
      formatter: (val: any) => (typeof val === "string" ? truncateLabel(val) : val),
    },
  },
  tooltip: {
    x: { formatter: (val: any, opts?: any) => categories[opts?.dataPointIndex ?? -1] ?? String(val) },
    y: { formatter: (val: number) => formatCurrency(val) },
  },
});

const barChartHeight = (count: number, rowHeight: number) => Math.max(220, count * rowHeight + 60);

const SEGMENT_LABEL_MAX_LENGTH = 24;

type SegmentItem = { label: string; value?: string; change: string; color: string };

const SegmentGrid = ({ items }: { items: SegmentItem[] }) => {
  const lastRowStart = items.length - (items.length % 2 === 0 ? 2 : 1);
  return (
    <div className="border rounded breakdown-grid">
      <div className="row g-0">
        {items.map((item, index) => (
          <div className="col-sm-6" key={`${item.label}-${index}`}>
            <div
              className={`p-3 bg-light h-100${index % 2 === 0 ? " border-end" : ""}${index < lastRowStart ? " border-bottom" : ""
                }`}
            >
              <p className="d-flex align-items-center mb-1 min-w-0">
                <i className="ti ti-circle-filled fs-8 me-1 flex-shrink-0" style={{ color: item.color }} />
                {item.label.length > SEGMENT_LABEL_MAX_LENGTH ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`rs-segment-${index}-${item.label}`}>{item.label}</Tooltip>}
                  >
                    <span className="text-truncate rs-segment-label">{item.label}</span>
                  </OverlayTrigger>
                ) : (
                  <span className="text-truncate">{item.label}</span>
                )}
              </p>
              {item.value !== undefined ? (
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <h5 className="main-title mb-0">{item.value}</h5>
                  <p
                    className={`fs-13 fw-medium d-inline-flex align-items-center mb-0 ${isNegativeGrowth(item.change) ? "text-danger" : "text-success"
                      }`}
                  >
                    <i className={`ti ${isNegativeGrowth(item.change) ? "ti-trending-down" : "ti-trending-up"} me-1`} />
                    {formatGrowth(item.change)}
                  </p>
                </div>
              ) : (
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <h5
                    className={`main-title d-inline-flex align-items-center mb-0 ${isNegativeGrowth(item.change) ? "text-danger" : "text-success"
                      }`}
                  >
                    <i className={`ti ${isNegativeGrowth(item.change) ? "ti-trending-down" : "ti-trending-up"} me-1`} />
                    {formatGrowth(item.change)}
                  </h5>
                  <span className="fs-13 text-muted">vs last period</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EmptyState = ({ message }: { message: string }) => (
  <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 rs-empty">
    <i className="ti ti-chart-bar-off fs-1 mb-2" />
    <p className="mb-0">{message}</p>
  </div>
);

const RevenueSummary = () => {
  useTemplateFont();
  const dispatch = useDispatch();
  const headerCollapse = useSelector((state: any) => state.commonSlice.headerCollapse);
  const { selectedTenantId, lifecycleTypeNames } = useContext(AuthContext);
  const names = useMemo(() => getLifecycleLabels(lifecycleTypeNames), [lifecycleTypeNames]);
  const salesUserId = useSalesUserId();

  const [data, setData] = useState<RevenueSummaryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const { filters, setFilters, defaults: defaultFilters } = useDashboardFilters();
  const [exporting, setExporting] = useState<ExportKind | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const [trendKey, setTrendKey] = useState<RevenuePeriod>("by_month");

  const apiFilters = useMemo(() => ({ ...toApiFilters(filters), salesUserId }), [filters, salesUserId]);
  const money = useMoneyFormat(filters.currencyCode);
  const formatCurrency = money.compact;

  useEffect(() => {
    setData(null);
  }, [selectedTenantId]);

  useEffect(() => {
    if (!selectedTenantId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchRevenueSummary(selectedTenantId, apiFilters)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load revenue summary");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenantId, reloadKey, apiFilters]);

  const reload = () => setReloadKey((key) => key + 1);

  const overviewStats = data?.overviewStats ?? [];

  const deal = data?.dealValue;
  const dealMax = Math.max(deal?.current ?? 0, deal?.previous ?? 0);

  const dealValueSeries = useMemo(
    () => [
      {
        name: `${names.deals} Average Value`,
        data:
          deal && dealMax > 0
            ? [(deal.current / dealMax) * 100, (deal.previous / dealMax) * 100]
            : [0, 0],
      },
    ],
    [deal, dealMax, names.deals]
  );

  const dealValueOptions = useMemo<ApexOptions>(
    () => ({
      chart: { type: "bar", sparkline: { enabled: true }, fontFamily: TEMPLATE_FONT_FAMILY },
      colors: [palette.dealCurrent, palette.dealPrevious],
      fill: { opacity: 1 },
      plotOptions: {
        bar: { columnWidth: "52%", distributed: true, borderRadius: 4, borderRadiusApplication: "around" },
      },
      states: { active: { filter: { type: "none" } } },
      xaxis: { ...sparklineXAxis, categories: ["Current", "Previous"] },
      yaxis: { show: false, min: 0, max: 100 },
      grid: { ...sparklineGrid },
      legend: { show: false },
      tooltip: {
        x: { show: true },
        marker: { show: false },
        y: {
          title: { formatter: () => "" },
          formatter: (_val: number, opts?: any) =>
            formatCurrency(
              opts?.dataPointIndex === 1 ? deal?.previous ?? 0 : deal?.current ?? 0
            ),
        },
      },
    }),
    [deal, formatCurrency]
  );

  const products = useMemo(
    () => [...(data?.products ?? [])].sort((a, b) => b.product_revenue - a.product_revenue),
    [data]
  );

  const productSeries = useMemo(
    () => [{ name: "Revenue", data: products.map((product) => product.product_revenue) }],
    [products]
  );

  const productOptions = useMemo<ApexOptions>(() => {
    const gradients = products.map((_, index) => productGradients[index % productGradients.length]);
    const base = horizontalBarBase(
      products.map((product) => product.product_name),
      niceMax(Math.max(0, ...products.map((product) => product.product_revenue)), 4),
      formatCurrency
    );
    return {
      ...base,
      colors: gradients.map((gradient) => gradient.from),
      fill: {
        type: "gradient",
        gradient: {
          type: "horizontal",
          shadeIntensity: 0,
          inverseColors: false,
          gradientToColors: gradients.map((gradient) => gradient.to),
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100],
        },
      },
    };
  }, [products, formatCurrency]);

  const productItems: SegmentItem[] = products.map((product, index) => ({
    label: product.product_name,
    change: product.growth,
    color: productGradients[index % productGradients.length].from,
  }));

  const trendPoints = useMemo(() => data?.trend[trendKey] ?? [], [data, trendKey]);

  const trendSeries = useMemo(
    () => [
      { name: "Actual Revenue", type: "area", data: trendPoints.map((point) => point.actual) },
      { name: "Prior Period", type: "area", data: trendPoints.map((point) => point.prior) },
    ],
    [trendPoints]
  );

  const trendOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "area",
        toolbar: { show: false },
        zoom: { enabled: true, type: "x", allowMouseWheelZoom: true },
        foreColor: palette.axis,
        fontFamily: TEMPLATE_FONT_FAMILY,
      },
      colors: [palette.actual, palette.prior],
      stroke: { curve: "smooth", width: 2 },
      fill: {
        type: "gradient",
        gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0, stops: [0, 100] },
      },
      dataLabels: { enabled: false },
      markers: { size: 0, hover: { size: 4 } },
      legend: { show: false },
      grid: { borderColor: palette.grid, strokeDashArray: 4 },
      xaxis: {
        categories: trendPoints.map((point) => formatTrendDate(point.date, trendKey)),
        tickPlacement: "on",
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { fontSize: "11px" } },
      },
      yaxis: {
        min: 0,
        max: niceMax(Math.max(0, ...trendPoints.map((point) => Math.max(point.actual, point.prior))), 6),
        tickAmount: 6,
        labels: { formatter: (val: number) => formatCurrency(val), style: { fontSize: "11px" } },
      },
      tooltip: { shared: true, intersect: false, y: { formatter: (val: number) => formatCurrency(val) } },
    }),
    [trendPoints, trendKey, formatCurrency]
  );

  const teams = useMemo(
    () => [...(data?.teams ?? [])].sort((a, b) => b.team_revenue - a.team_revenue),
    [data]
  );

  const teamSeries = useMemo(
    () => [{ name: "Revenue", data: teams.map((team) => team.team_revenue) }],
    [teams]
  );

  const teamOptions = useMemo<ApexOptions>(
    () => ({
      ...horizontalBarBase(
        teams.map((team) => team.team_name),
        niceMax(Math.max(0, ...teams.map((team) => team.team_revenue)), 4),
        formatCurrency
      ),
      colors: teams.map((_, index) => teamColors[index % teamColors.length]),
      fill: { opacity: 1 },
    }),
    [teams, formatCurrency]
  );

  const teamItems: SegmentItem[] = teams.map((team, index) => ({
    label: team.team_name,
    value: formatCurrency(team.team_revenue),
    change: team.growth,
    color: teamColors[index % teamColors.length],
  }));

  const comparisonRows = (data?.comparison ?? [])
    .filter((item) => comparisonMeta[item.key])
    .map((item) => {
      const meta = comparisonMeta[item.key];
      const format = (value: number) => `${countFormatter.format(value)}${meta.unit ? ` ${meta.unit}` : ""}`;
      return {
        key: item.key,
        label: typeof meta.label === "function" ? meta.label(names) : meta.label,
        className: meta.className,
        value: format(item.value),
        previous: format(item.previous),
        change: formatGrowth(item.growth),
      };
    });

  const handleExport = async (kind: ExportKind) => {
    const container = reportRef.current;
    if (!selectedTenantId || !data || exporting) return;
    setExporting(kind);
    try {
      if (kind === "pdf") {
        if (!container) return;
        await new Promise((resolve) => setTimeout(resolve, 150));
        await exportDashboardPdf({
          container,
          sectionSelector: ".rs-pdf-section",
          title: "Revenue Summary",
          filtersText: describeFilters(filters, true),
          fileName: `revenue-summary-${dayjs().format("YYYY-MM-DD")}.pdf`,
        });
      } else {
        const file = await exportRevenueSummaryExcel(selectedTenantId, apiFilters);
        await downloadExportFile(file, `revenue-summary-${dayjs().format("YYYY-MM-DD")}`);
      }
    } catch (err) {
      setError((err as Error)?.message || `Failed to export ${kind === "pdf" ? "PDF" : "Excel"}`);
    } finally {
      setExporting(null);
    }
  };

  const header = (
    <>
      <div
        className={`d-flex align-items-center justify-content-between gap-2 flex-wrap ${hasFilterChips(filters, defaultFilters, true) ? "mb-3" : "mb-4"
          }`}
      >
        <div>
          <h4 className="mb-0">Revenue Summary</h4>
        </div>
        <div className="gap-2 d-flex align-items-center flex-wrap">
          {selectedTenantId && (
            <DashboardFilterDropdown
              title="Revenue Filters"
              filters={filters}
              onChange={setFilters}
              loading={loading}
              showDefaultDateRange
            />
          )}
          <ExportDropdown
            disabled={!data || loading}
            exporting={exporting}
            spinClassName="rs-spin"
            onExport={handleExport}
          />
          <HeaderAction
            label="Refresh"
            icon={`ti ti-refresh${loading ? " rs-spin" : ""}`}
            onClick={reload}
          />
          <HeaderAction
            label={headerCollapse ? "Expand" : "Collapse"}
            icon="ti ti-transition-top"
            onClick={() => dispatch(setHeaderCollapse(!headerCollapse))}
          />
        </div>
      </div>
      <ActiveFilterChips filters={filters} onChange={setFilters} disabled={loading} showDefaultDateRange />
    </>
  );

  if (!selectedTenantId) {
    return (
      <div className="page-wrapper revenue-summary">
        <div className="content pb-0">
          {header}
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="ti ti-building-community fs-1 text-muted mb-3" />
              <h5>Select a Tenant</h5>
              <p className="text-muted mb-0">Please select a tenant to view the revenue summary.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data && error && !loading) {
    return (
      <div className="page-wrapper revenue-summary">
        <div className="content pb-0">
          {header}
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="ti ti-alert-circle fs-1 text-danger mb-3" />
              <h5>Unable to load revenue summary</h5>
              <p className="text-muted mb-3">{error}</p>
              <button type="button" className="btn btn-outline-light" onClick={reload}>
                <i className="ti ti-refresh me-1" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="page-wrapper revenue-summary">
        <div className="content pb-0" aria-busy="true">
          {header}
          <span className="visually-hidden" role="status">
            Loading revenue summary
          </span>
          <RevenueSummarySkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper revenue-summary">
      <div className="content pb-0">
        {header}

        {error && (
          <div className="alert alert-danger d-flex align-items-center justify-content-between gap-2 mb-4">
            <span>{error}</span>
            <button type="button" className="btn btn-sm btn-outline-light" onClick={reload}>
              Try Again
            </button>
          </div>
        )}

        <div ref={reportRef}>
          <div className="row rs-pdf-section">
            <div className="col-xxl-7 d-flex">
              <div className="row w-100 mx-0">
                <div className="col-md-12 d-flex px-0">
                  <div className="card flex-fill">
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                        <h5 className="mb-0 fs-18 fw-bold d-inline-flex align-items-center">Overview Statistics</h5>
                        {/* <Link to={route.analytics} className="btn btn-sm btn-icon btn-outline-light border">
                        <i className="ti ti-arrow-right" />
                      </Link> */}
                      </div>
                      {overviewStats.length ? (
                        <div className="border rounded">
                          <div className="row g-4">
                            {overviewStats.map((stat, index) => {
                              const meta = overviewMeta[stat.key] ?? defaultOverviewMeta;
                              const growth = stat.growth;
                              return (
                                <div
                                  className={`col-md-4 d-flex ${overviewColumnClasses[index] ?? ""}`}
                                  key={stat.key}
                                >
                                  <div
                                    className={`p-3 card-hover text-center mb-0 flex-fill${index < overviewStats.length - 1 ? " border-end" : ""
                                      }`}
                                  >
                                    <div className={`avatar avatar-md ${meta.avatarClass} fs-16 mb-2`}>
                                      <i className={`${meta.currencyIcon ? money.icon : meta.icon} fs-22`} />
                                    </div>
                                    <p className="mb-1">{meta.label ? meta.label(names) : stat.display_name}</p>
                                    <h5 className={stat.count !== null ? "mb-1" : "mb-3"}>{formatCurrency(stat.amount)}</h5>
                                    {stat.count !== null && (
                                      <p className="fs-13 text-muted mb-2">
                                        {countFormatter.format(stat.count)} {meta.countLabel ? meta.countLabel(names) : ""}
                                      </p>
                                    )}
                                    <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                                      <span
                                        className={`d-inline-flex align-items-center badge rounded-pill ${growthBadgeClass(
                                          growth
                                        )} border-0`}
                                      >
                                        {formatGrowth(growth)}
                                      </span>
                                      <p className="text-dark mb-0">vs Last Period</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <EmptyState message="No overview statistics available" />
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-md-12 d-flex px-0">
                  <div className="card flex-fill">
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                        <h5 className="mb-0">{names.deals} Average Value</h5>
                      </div>
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                          <div className="mb-2">
                            <p className="d-flex align-items-center mb-1">
                              <i className="ti ti-square-filled fs-8 deal-current-marker me-1" />
                              Current Period
                            </p>
                            <h5 className="main-title mb-0">{formatCurrency(deal?.current ?? 0)}</h5>
                          </div>
                          <div className="mb-0">
                            <p className="d-flex align-items-center mb-1">
                              <i className="ti ti-square-filled fs-8 deal-previous-marker me-1" />
                              Previous Period
                            </p>
                            <h5 className="main-title mb-0">{formatCurrency(deal?.previous ?? 0)}</h5>
                          </div>
                        </div>
                        <Chart options={dealValueOptions} series={dealValueSeries} type="bar" width={70} height={90} />
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span
                          className={`d-inline-flex align-items-center badge rounded-pill ${growthBadgeClass(
                            deal?.growth
                          )} border-0`}
                        >
                          {formatGrowth(deal?.growth)}
                        </span>
                        <p className="text-dark mb-0">From Last Period</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xxl-5 d-flex flex-column">
              <div className="row flex-fill">
                <div className="col-md-12 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <h5 className="mb-0">Revenue Breakdown by Product & Service</h5>
                      </div>
                      {products.length ? (
                        <>
                          <Chart
                            options={productOptions}
                            series={productSeries}
                            type="bar"
                            height={barChartHeight(products.length, 46)}
                          />
                          <SegmentGrid items={productItems} />
                        </>
                      ) : (
                        <EmptyState message="No product or service revenue available" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row rs-pdf-section">
            <div className="col-12">
              <div className="card">
                <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="mb-0 fs-18 fw-bold text-dark d-flex align-items-center gap-2">
                    Revenue Performance Trend
                    {/* <button type="button" className="btn btn-sm btn-icon btn-outline-light border" onClick={reload}>
                    <i className={`ti ti-refresh${loading ? " rs-spin" : ""}`} />
                  </button> */}
                  </div>
                  <GranularityToggle value={trendKey} onChange={setTrendKey} />
                </div>
                <div className="card-body pt-0">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <span>Comparing actual revenue vs. prior period</span>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className="fw-medium border rounded text-gray-5 d-flex align-items-center px-2 gap-1">
                        <i className="ti ti-circle-filled fs-8 text-info" /> Actual Revenue
                      </span>
                      <span className="fw-medium border rounded text-gray-5 d-flex align-items-center px-2 gap-1">
                        <i className="ti ti-circle-filled fs-8 text-danger" /> Prior Period
                      </span>
                    </div>
                  </div>
                  {trendPoints.length ? (
                    <Chart options={trendOptions} series={trendSeries} type="area" height={330} />
                  ) : (
                    <EmptyState message="No revenue trend data for this period" />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row rs-pdf-section">
            <div className="col-xl-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header border-0 d-flex align-items-center justify-content-between">
                  <div className="mb-0 fs-18 fw-bold text-dark">Revenue Breakdown by Team</div>
                </div>
                <div className="card-body pt-0">
                  {teams.length ? (
                    <>
                      <Chart options={teamOptions} series={teamSeries} type="bar" height={barChartHeight(teams.length, 56)} />
                      <SegmentGrid items={teamItems} />
                    </>
                  ) : (
                    <EmptyState message="No team revenue available" />
                  )}
                </div>
              </div>
            </div>
            <div className="col-xl-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header border-0">
                  <div className="mb-0 fs-18 fw-bold text-dark">{names.deal} Closing Performance</div>
                  <p className="fs-13 text-muted mb-0">
                    {names.deals} closed and average sales cycle compared with the previous period
                  </p>
                </div>
                <div className="card-body pt-0">
                  {comparisonRows.length ? (
                    comparisonRows.map((item) => (
                      <div
                        key={item.key}
                        className={`p-4 d-flex justify-content-between align-items-center ${item.className}`}
                      >
                        <div className="row w-100 g-1">
                          <div className="col-6">
                            <p className="mb-1 fs-14 text-dark">{item.label}</p>
                            <h2 className="fw-bold mb-0 fs-20">{item.value}</h2>
                          </div>
                          <div className="col-4">
                            <p className="mb-1 fs-14 text-dark">Previous</p>
                            <div className="fw-bold mb-0 fs-20 text-dark">{item.previous}</div>
                          </div>
                          <div className="col-2 d-flex align-items-center justify-content-md-end mt-3 mt-md-0">
                            <span className="badge rounded-pill bg-white text-dark py-2 fs-12">{item.change}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <EmptyState message="No comparison data for this period" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueSummary;
